import { Box, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material"
import type { FC } from "react"
import { Controller, type FieldArrayWithId, useFormContext } from "react-hook-form"

import FileArea from "@/components/upload/components/FileArea"
import {
  ALLOWED_TYPE_LABEL_IMAGES_WITH_PDF_AND_DOC,
  IMAGE_MIME_TYPES_WITH_PDF_AND_DOC,
} from "@/components/upload/constants"
import {
  FIELD_ERROR_CLASSES,
  FIELD_LABEL_CLASSES,
  SELECT_STYLING_PROPS,
  TEXT_AREA_STYLING_PROPS,
} from "@/constants/forms"
import type { FieldGroupEnum } from "@/graphql/codegen/graphql"
import { ReferenceTableName } from "@/graphql/codegen/graphql"
import { PURCHASE_ORDER_FILE_TYPE_TO_LABEL } from "@/screens/Procurement/PurchaseOrder/constants"

interface PurchaseOrderFileFormProps {
  fields: FieldArrayWithId[]
}

export const PurchaseOrderFileForm: FC<PurchaseOrderFileFormProps> = ({ fields }) => {
  const { control } = useFormContext()

  return (
    <form>
      {fields.map((purchaseOrderFileField, index) => (
        <Box key={purchaseOrderFileField.id} className='mb-10'>
          <Controller
            name={`purchaseOrderFiles.${index}.fileType`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth data-testid={`purchaseOrderFiles.${index}.fileType`}>
                <InputLabel id={`purchaseOrderFiles.${index}.fileType`} className={FIELD_LABEL_CLASSES}>
                  Document Type<span className='text-red-600'>*</span>
                </InputLabel>
                <Select
                  {...field}
                  {...SELECT_STYLING_PROPS}
                  labelId={`purchaseOrderFiles.${index}.fileType`}
                  renderValue={(key) => {
                    if (!key) {
                      return <span className='text-gray-500'>Select One</span>
                    }
                    return PURCHASE_ORDER_FILE_TYPE_TO_LABEL.get(key as FieldGroupEnum) ?? ""
                  }}
                  error={!!error}
                >
                  {Array.from(PURCHASE_ORDER_FILE_TYPE_TO_LABEL).map(([key, value]) => (
                    <MenuItem key={key} value={key}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
                {error?.message && (
                  <p className={FIELD_ERROR_CLASSES} data-testid='location-code-error'>
                    {error.message}
                  </p>
                )}
              </FormControl>
            )}
          />

          <Controller
            name={`purchaseOrderFiles.${index}.description`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Box className='mt-3' data-testid={`purchaseOrderFiles.${index}.description`}>
                <InputLabel id={`purchaseOrderFiles.${index}.description`} className={FIELD_LABEL_CLASSES}>
                  Document Description<span className='text-red-600'>*</span>
                </InputLabel>
                <TextField
                  {...field}
                  {...TEXT_AREA_STYLING_PROPS}
                  id={`purchaseOrderFiles.${index}.description`}
                  placeholder='Document Description'
                  error={!!error}
                />
                {error?.message && (
                  <p className={FIELD_ERROR_CLASSES} data-testid='location-code-error'>
                    {error.message}
                  </p>
                )}
              </Box>
            )}
          />
          <FileArea
            name={`purchaseOrderFiles.${index}.file`}
            fieldGroups={Array.from(PURCHASE_ORDER_FILE_TYPE_TO_LABEL.keys())}
            referenceTableName={ReferenceTableName.Order}
            showUploadAreaByDefault
            allowedMimeTypes={IMAGE_MIME_TYPES_WITH_PDF_AND_DOC}
            allowedTypesLabel={ALLOWED_TYPE_LABEL_IMAGES_WITH_PDF_AND_DOC}
            uploadAreaTitle='Click to upload'
          />
        </Box>
      ))}
    </form>
  )
}
